// FieldRenderer - S47 / S48.1.
// Renderiza un field segun su type/bindKey con QR/Code 128 reales (S47.3b).
// S48.1: acepta prop `bundle` ademas de `producto`. valueForField recibe
// ambos y dispatcha por prefijo del bindKey.
import { useEffect, useRef } from 'react';
import { ensureKeyframes, injectCSS } from '../../../design/motion';
import { valueForField } from '../dynamicFields';
import {
  drawQrOnCanvas,
  drawCode128OnCanvas,
} from '../render/canvas_renderer';

const CSS_ID = 'xl-et-fr-css';
const CSS = `
.xl-et-fr {
  width: 100%; height: 100%;
  display: flex; align-items: center; justify-content: center;
  padding: 2px;
  font-family: var(--xl-font-sans);
  color: var(--xl-color-text);
  font-size: 11px; line-height: 1.1;
  text-align: center;
  overflow: hidden;
  user-select: none;
  box-sizing: border-box;
}
.xl-et-fr--text  { font-weight: 500 }
.xl-et-fr--sku   { font-family: var(--xl-font-mono); letter-spacing: 0.02em }
.xl-et-fr--price { font-family: var(--xl-font-mono); font-weight: 700 }
.xl-et-fr--canvas { padding: 0 }
.xl-et-fr__cv { width: 100%; height: 100%; display: block }
`;

const PREVIEW_QR_PX = 200;
const PREVIEW_BC_W_PX = 400;
const PREVIEW_BC_H_PX = 100;

export default function FieldRenderer({ field, producto, bundle }) {
  // Hooks SIEMPRE antes de cualquier early return (Rules of Hooks).
  useEffect(() => {
    ensureKeyframes();
    injectCSS(CSS_ID, CSS);
  }, []);

  const canvasRef = useRef(null);

  const t = field ? field.type : null;
  const value = field ? valueForField(field, producto, bundle) : '';
  const isQr = (t === 'qr');
  const isBc = (t === 'barcode');

  useEffect(() => {
    if (!isQr && !isBc) return;
    const cv = canvasRef.current;
    if (!cv) return;
    const ctx = cv.getContext('2d');
    if (!ctx) return;
    if (isQr) {
      cv.width = PREVIEW_QR_PX;
      cv.height = PREVIEW_QR_PX;
      ctx.clearRect(0, 0, PREVIEW_QR_PX, PREVIEW_QR_PX);
      drawQrOnCanvas(ctx, value, 0, 0, PREVIEW_QR_PX, PREVIEW_QR_PX);
    } else {
      cv.width = PREVIEW_BC_W_PX;
      cv.height = PREVIEW_BC_H_PX;
      ctx.clearRect(0, 0, PREVIEW_BC_W_PX, PREVIEW_BC_H_PX);
      drawCode128OnCanvas(ctx, value, 0, 0, PREVIEW_BC_W_PX, PREVIEW_BC_H_PX);
    }
  }, [isQr, isBc, value]);

  if (!field) return null;

  if (isQr) {
    return (
      <div className="xl-et-fr xl-et-fr--canvas" aria-label={'QR ' + value}>
        <canvas ref={canvasRef} className="xl-et-fr__cv" />
      </div>
    );
  }
  if (isBc) {
    return (
      <div className="xl-et-fr xl-et-fr--canvas" aria-label={'Barcode ' + value}>
        <canvas ref={canvasRef} className="xl-et-fr__cv" />
      </div>
    );
  }
  if (t === 'price') {
    return <div className="xl-et-fr xl-et-fr--price">{value}</div>;
  }
  if (t === 'sku') {
    return <div className="xl-et-fr xl-et-fr--sku">{value}</div>;
  }
  return <div className="xl-et-fr xl-et-fr--text">{value}</div>;
}
